import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import Brand from "@/components/Brand";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reset failed");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background">
        <div className="max-w-sm text-center space-y-3">
          <h2 className="font-heading text-2xl font-bold">Invalid link</h2>
          <p className="text-sm text-muted-foreground">This reset link is missing a token.</p>
          <Link to="/forgot-password"><Button className="rounded-full">Request new link</Button></Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-background">
        <div className="max-w-sm w-full text-center space-y-4" data-testid="reset-done">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center">
            <CheckCircle size={28} weight="duotone" className="text-emerald-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold">Password updated</h2>
          <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="reset-form" autoComplete="off">
        <Brand />
        <div>
          <div className="overline text-muted-foreground mb-2">New credential</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Reset password</h2>
          <p className="text-sm text-muted-foreground mt-1">Pick a strong new password.</p>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">NEW PASSWORD</Label>
            <div className="relative mt-1.5">
              <Input type={show ? "text" : "password"} value={password}
                onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                placeholder="At least 8 characters" data-testid="reset-password-input" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">CONFIRM PASSWORD</Label>
            <Input type={show ? "text" : "password"} value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password"
              placeholder="Repeat password" className="mt-1.5" data-testid="reset-confirm-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="reset-submit-button">
          {loading ? "Updating…" : "Update password"}
        </Button>
      </form>
    </div>
  );
}
